import React, { useState, useEffect } from "react";
import { Col, FormGroup, Input, Label, Row, Card } from "reactstrap";
import { LabelWithToolTip } from "../../../../atoms/LabelWithToolTip";
import Select from "react-select";
import LockFieldsButton from "../../../../molecules/LockFieldsButton";

const AbaRoutingNumberForm = (props) => {
  const getCurrentCurrency = () => {
    const usdCurrency = props.sortedCurrencyList?.find(c => c.currencyCode === 'USD');
    return usdCurrency?.currencyCode || '';
  };

  const [selectedCurrencyName, setSelectedCurrencyName] = useState(getCurrentCurrency());

  useEffect(() => {
    const usdCurrency = props.sortedCurrencyList?.find(c => c.currencyCode === 'USD');
    if (usdCurrency) {
      setSelectedCurrencyName(usdCurrency.currencyCode);
      const event = { target: { value: usdCurrency.id } };
      props.onCurrencyChanged(event);
    }
  }, [props.sortedCurrencyList]);

  const handleCurrencyChange = (e) => {
    const currencyId = e.target.value;
    const currencyName = props.sortedCurrencyList?.find(c => c.id == currencyId);

    setSelectedCurrencyName(currencyName?.currencyCode || '');

    props.onCurrencyChanged(e);
  };

  return (
    <>
      {/* Purpose */}
      <FormGroup>
        <LabelWithToolTip
          id="labelPurpose"
          labelText="Purpose *"
          tooltipText="How this account will be used: Payroll = salary payments; Expenditure = expenses and reimbursements; All = both payroll and expenditure."
        />
        <Select
          value={props.purpose ? props.purposeOption.find((item) => item.value === props.purpose) : null}
          onChange={(e) => props.setPurpose(e?.value)}
          options={props.purposeOption}
          placeholder="Select..."
        />
      </FormGroup>

      {/* Currency Selection */}
      <Row>
        <Col sm="12" md="6">
          <FormGroup>
            <LabelWithToolTip
              id="labelCurrency"
              labelText="Currency"
              tooltipText="ABA Routing Numbers are US-specific and are locked to USD."
            />
            <select
              {...props.register("currencyId")}
              className="form-control"
              value={
                props.sortedCurrencyList?.find((c) => c.currencyCode === 'USD')?.id || ""
              }
              onChange={handleCurrencyChange}
              disabled={true}
            >
              {(props.sortedCurrencyList || []).map((currency) => (
                <option key={currency.id} value={currency.id}>
                  {currency.currencyCode}
                </option>
              ))}
            </select>
          </FormGroup>
        </Col>
      </Row>


      {/* Account Type */}
      <Row>
        <Col sm="12" md="6">
          <FormGroup>
            <Label for="accountType">Account type *</Label>
            <Select
              value={props.accountTypeOptions?.find((item) => item.value === props.accountType) || null}
              onChange={(e) => props.setAccountType(e?.value)}
              options={props.accountTypeOptions}
              placeholder="Select..."
            />
          </FormGroup>
        </Col>
      </Row>


      {/* Account Number */}
      <Row>
        <Col sm="12" md="12">
          <FormGroup>
            <LabelWithToolTip
              id="lblAccountNumber"
              labelText="Account Number *"
              tooltipText="Your bank account number as it appears on your bank statement or cheque. Only digits allowed; hyphens are not permitted."
            />
            <input
              type="text"
              {...props.register("bankAccountNumber", { required: true })}
              className="form-control"
              onChange={(e) => {
                const stripped = e.target.value.replace(/-/g, "");
                props.setValue("bankAccountNumber", stripped);
                props.resetMetadata(e);
              }}
            />
          </FormGroup>
        </Col>
      </Row>


      {/* Beneficiary Name */}
      <Row>
        <Col sm="12" md="12">
          <FormGroup>
            <LabelWithToolTip
              id="lblBeneficiaryName"
              labelText="Beneficiary Name *"
              tooltipText="Name of the account holder as it appears on the bank account. Only alphanumeric characters, spaces, parentheses, commas and periods. Ampersands (&) are not permitted."
            />
            <input
              type="text"
              {...props.register("beneficiaryBankAccountName", { required: true })}
              className="form-control"
              onChange={props.resetMetadata}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* ABA Routing Number */}
      <Row>
        <Col sm="12" md="12">
          <FormGroup>
            <LabelWithToolTip
              id="lblAbaRoutingNumber"
              labelText="ABA Routing Number *"
              tooltipText="9-digit ABA Routing Number (also known as ABA RTN or Routing Transit Number) used to identify US financial institutions."
            />
            <input
              type="text"
              maxLength="9"
              name="abaRoutingNumber"
              value={props.abaRoutingNumber || ""}
              onChange={(e) => {
                props.setValue("abaRoutingNumber", e.target.value);
                props.resetMetadata(e);
              }}
              className="form-control"
            />
          </FormGroup>
        </Col>
      </Row>



      {/* Auto-filled fields - only visible if bank selected from MyHR OR after manual ABA validation */}
      {(props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated) && (
        <>
          {/* Lock/Unlock Button */}
          <LockFieldsButton allowEdit={props.allowEdit} setAllowEdit={props.setAllowEdit} />

          {/* Bank Name */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>Bank Name</Label>
                <input
                  type="text"
                  {...props.register("bankName", { required: false })}
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* City */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>City</Label>
                <input
                  type="text"
                  {...props.register("bankCity", { required: false })}
                  className="form-control"
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* State */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>State</Label>
                <input
                  type="text"
                  {...props.register("state", { required: false })}
                  className="form-control"
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* County */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>County</Label>
                <input
                  type="text"
                  {...props.register("county", { required: false })}
                  className="form-control"
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Zip Code */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>Zip Code</Label>
                <input
                  type="text"
                  {...props.register("bankZip", { required: false })}
                  className="form-control"
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Country */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>Country</Label>
                <input
                  type="text"
                  {...props.register("bankCountry", { required: false })}
                  className="form-control"
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Timezone */}
          <Row>
            <Col sm="12" md="12">
              <FormGroup>
                <Label>Timezone</Label>
                <input
                  type="text"
                  {...props.register("timezone", { required: false })}
                  className="form-control"
                  disabled={!props.allowEdit && (props.selectedBankFromMyHR || props.isAbaRoutingNumberValidated)}
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};


export default AbaRoutingNumberForm;